import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { createAuthUser, otpFieldHandlers, totpDisable, totpEnable, totpSetup } from './api/auth';
import './AccountPanels.css';

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 3l18 18M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-1.2M9.9 5.1A10.4 10.4 0 0 1 12 5c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.2 3.1M6.1 6.1C3.9 7.7 2 12 2 12s3.5 7 10 7c1.4 0 2.7-.3 3.9-.8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function PasswordField({ value, onChange, autoComplete, minLength, required, id }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="account-modal__password">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        minLength={minLength}
        required={required}
      />
      <button
        type="button"
        className="account-modal__eye"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        title={visible ? 'Hide password' : 'Show password'}
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

function ModalShell({ title, subtitle, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="account-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="account-modal-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="account-modal__card">
        <div className="account-modal__head">
          <div>
            <h2 id="account-modal-title" className="account-modal__title">
              {title}
            </h2>
            {subtitle ? <p className="account-modal__sub">{subtitle}</p> : null}
          </div>
          {onClose ? (
            <button
              type="button"
              className="account-modal__close"
              onClick={onClose}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
}

export function CreateUserPanel({ isAdmin, onClose }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [makeAdmin, setMakeAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  if (!isAdmin) return null;

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setOk('');
    try {
      const res = await createAuthUser({
        email,
        password,
        is_admin: makeAdmin,
      });
      setOk(`Created ${res.user?.email || email}`);
      setEmail('');
      setPassword('');
      setMakeAdmin(false);
    } catch (err) {
      setError(err.message || 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title="Create user"
      subtitle="Add a dashboard login. They can sign in with email code or authenticator."
      onClose={onClose}
    >
      <form className="account-modal__form" onSubmit={onSubmit}>
        {error ? <p className="account-modal__error">{error}</p> : null}
        {ok ? <p className="account-modal__ok">{ok}</p> : null}
        <label>
          Email
          <input
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            required
          />
        </label>
        <label>
          Password
          <PasswordField
            id="create-user-password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
          />
        </label>
        <label className="account-modal__check">
          <input
            type="checkbox"
            checked={makeAdmin}
            onChange={(e) => setMakeAdmin(e.target.checked)}
          />
          Grant admin access
        </label>
        <div className="account-modal__actions">
          {onClose ? (
            <button type="button" className="account-modal__btn account-modal__btn--ghost" onClick={onClose}>
              Cancel
            </button>
          ) : null}
          <button type="submit" className="account-modal__btn" disabled={busy}>
            {busy ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

export function TotpSetupPanel({ totpEnabled, onUpdated, onClose }) {
  const [secret, setSecret] = useState('');
  const [uri, setUri] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  async function startSetup() {
    setBusy(true);
    setError('');
    setOk('');
    try {
      const res = await totpSetup();
      setSecret(res.secret || '');
      setUri(res.otpauth_uri || '');
    } catch (err) {
      setError(err.message || 'Setup failed');
    } finally {
      setBusy(false);
    }
  }

  async function enable(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await totpEnable(code);
      setOk('Authenticator enabled.');
      onUpdated?.({ totp_enabled: true });
    } catch (err) {
      setError(err.message || 'Enable failed');
    } finally {
      setBusy(false);
    }
  }

  async function disable(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await totpDisable({ password, code });
      setOk('Authenticator disabled.');
      setSecret('');
      setUri('');
      onUpdated?.({ totp_enabled: false });
    } catch (err) {
      setError(err.message || 'Disable failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title="Authenticator"
      subtitle="Optional. After enabling, you can sign in with an app code instead of email."
      onClose={onClose}
    >
      {error ? <p className="account-modal__error">{error}</p> : null}
      {ok ? <p className="account-modal__ok">{ok}</p> : null}

      {!totpEnabled ? (
        <>
          {!secret ? (
            <div className="account-modal__actions">
              <button
                type="button"
                className="account-modal__btn account-modal__btn--ghost"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="button" className="account-modal__btn" onClick={startSetup} disabled={busy}>
                {busy ? 'Please wait…' : 'Set up authenticator'}
              </button>
            </div>
          ) : (
            <form className="account-modal__form" onSubmit={enable}>
              <p className="account-modal__hint">
                Scan this QR with Google Authenticator or Authy, then enter a code.
              </p>
              {uri ? (
                <div className="account-modal__qr" aria-label="Authenticator QR code">
                  <QRCodeSVG value={uri} size={180} bgColor="#ffffff" fgColor="#111111" />
                </div>
              ) : null}
              <p className="account-modal__hint">Or enter this key manually:</p>
              <code className="account-modal__secret">{secret}</code>
              <label>
                Authenticator code
                <input
                  value={code}
                  {...otpFieldHandlers(setCode)}
                  required
                />
              </label>
              <div className="account-modal__actions">
                <button
                  type="button"
                  className="account-modal__btn account-modal__btn--ghost"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button type="submit" className="account-modal__btn" disabled={busy}>
                  {busy ? 'Enabling…' : 'Enable'}
                </button>
              </div>
            </form>
          )}
        </>
      ) : (
        <form className="account-modal__form" onSubmit={disable}>
          <p className="account-modal__hint">Authenticator is enabled for this account.</p>
          <label>
            Password
            <PasswordField
              id="disable-totp-password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <label>
            Current authenticator code
            <input
              value={code}
              {...otpFieldHandlers(setCode)}
              required
            />
          </label>
          <div className="account-modal__actions">
            <button
              type="button"
              className="account-modal__btn account-modal__btn--ghost"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="account-modal__btn" disabled={busy}>
              {busy ? 'Disabling…' : 'Disable authenticator'}
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}
